import { debounce } from 'throttle-debounce'
import { createMusicIDB, deduplicateArray } from './utils'

const STEP = 10
const MUSIC_LIST_KEY = '--music-list--'
const PLAYER_STATE_KEY = '--music-player-state--'
const LEGACY_LYRICS_MAP_KEY = '--music-lyrics-map--'
const AUDIO_EXT_REGEXP = /\.(mp3|wav|ogg|webm)$/i

export default class Music {
  constructor() {
    this.audio = new Audio()
    this.items = JSON.parse(localStorage.getItem(MUSIC_LIST_KEY)) || []
    const savedPlayerState = getSavedPlayerState(this.items.length)
    localStorage.removeItem(LEGACY_LYRICS_MAP_KEY)
    this.playQueue = []
    this.playedQueue = []
    this.searchItems = []
    this.searchCursorIndex = 0
    this.index = savedPlayerState.index
    this.cursorIndex = savedPlayerState.index
    this.random = savedPlayerState.random
    this.loop = savedPlayerState.loop
    this.paused = true
    this.volume = savedPlayerState.volume
    this.currentTime = 0
    this.duration = 0
    this.changingProgress = false
    this.progress = '00:00/00:00'
    this.audio.loop = this.loop
    this.audio.volume = this.volume
    this.setChangingProgress = debounce(
      1000,
      false,
      function (changingProgress) {
        this.changingProgress = changingProgress
      }
    )
    this.idb = createMusicIDB()
  }
  init() {
    this.audio.onended = () => {
      this.next()
    }
    this.audio.ontimeupdate = () => {
      this.syncProgress()
    }
    this.audio.onloadedmetadata = () => {
      this.syncProgress()
    }
    this.audio.onplay = () => {
      this.paused = false
    }
    this.audio.onpause = () => {
      this.paused = true
    }
  }
  addItems() {
    const fileEl = document.createElement('input')
    fileEl.type = 'file'
    fileEl.multiple = true
    fileEl.accept = '.mp3,.wav,.ogg,.webm'
    fileEl.click()
    fileEl.onchange = async () => {
      const selectedFiles = [...fileEl.files]
      const files = selectedFiles.filter((file) => isAudioFile(file))
      const itemsNames = this.items.map((item) => item.name)
      const fileNames = files.map((file) => file.name)
      this.items = deduplicateArray([...itemsNames, ...fileNames]).map(
        (name, index) => ({
          name,
          index,
        })
      )
      localStorage.setItem(MUSIC_LIST_KEY, JSON.stringify(this.items))
      this._savePlayerState()
      files.forEach((file) => {
        this.idb.addData(file)
      })
    }
  }
  removeItems() {
    if (confirm('确认清空播放列表?')) {
      this.items = []
      this.cursorIndex = this.index = 0
      this.paused = true
      this.currentTime = 0
      this.duration = 0
      localStorage.removeItem(MUSIC_LIST_KEY)
      this._savePlayerState()
      this.idb.clearData()
    }
  }
  pushPlayQueue(index) {
    this.playQueue = [...this.playQueue, index]
  }
  popPlayQueue(index) {
    this.playQueue = this.playQueue.filter((idx) => idx !== index)
  }
  async setCurrent(index, opts = {}) {
    if (!this.items.length) {
      this.stop()
      return
    }
    const { play = true, isPreAction = false } = opts
    if (index !== undefined) {
      this.index = index
    }
    this._correntIndex()
    this._savePlayerState()
    if (!isPreAction) {
      this.playedQueue.push(this.index)
    }
    this.cursorIndex = this.index
    const key = this.items[this.index].name
    this.audio.src = URL.createObjectURL(await this.idb.getData(key))
    play && this.play()
  }
  playCurrentCursor() {
    if (!this.items.length) {
      return
    }
    this.setCurrent(this.cursorIndex)
  }
  play() {
    if (!this.items.length) {
      return
    }
    if (!this.audio.src) {
      this.setCurrent(undefined, { play: false })
    }
    this.audio.play()
    this.paused = false
  }
  pause() {
    this.audio.pause()
    this.paused = true
  }
  playOrPause() {
    if (this.audio.paused) {
      this.play()
    } else {
      this.pause()
    }
  }
  stop() {
    this.pause()
    this.audio.currentTime = 0
    this.syncProgress()
    this.paused = true
  }

  _correntIndex() {
    if (!this.index) {
      this.index = 0
    }
    if (this.index < 0) {
      this.index = this.items.length - 1
    } else if (this.index >= this.items.length) {
      this.index = 0
    }
    return this.index
  }

  pre() {
    if (!this.items.length) {
      return
    }
    if (this.playedQueue.length > 0) {
      const index = this.playedQueue.pop()
      if (index === this.index) {
        this.index = this.playedQueue.pop()
      } else {
        this.index = index
      }
      if (this.index === undefined) {
        this.index--
      }
      this.playedQueue = [...this.playedQueue]
    } else {
      this.index--
    }

    this._correntIndex()
    this.setCurrent(this.index, { isPreAction: true })
  }
  next() {
    if (!this.items.length) {
      return
    }
    if (this.playQueue.length) {
      this.index = this.playQueue.shift()
      this.playQueue = [...this.playQueue]
    } else {
      if (this.random) {
        this.index = this._getRandomIndex()
      } else {
        this.index++
      }
    }

    this._correntIndex()
    this.setCurrent(this.index)
  }
  cursorUp() {
    if (!this.items.length) {
      this.cursorIndex = 0
      return
    }
    if (this.cursorIndex > 0) {
      this.cursorIndex--
    } else {
      this.cursorIndex = this.items.length - 1
    }
  }
  cursorDown() {
    if (!this.items.length) {
      this.cursorIndex = 0
      return
    }
    if (this.cursorIndex < this.items.length - 1) {
      this.cursorIndex++
    } else {
      this.cursorIndex = 0
    }
  }
  chaneProgress() {
    this.syncProgress()
    this.progress = `${formatTime(this.currentTime)}/${formatTime(this.duration)}`
    this.changingProgress = true
    this.setChangingProgress(false)
  }
  backward() {
    if (!this.items.length) {
      return
    }
    if (this.audio.currentTime > STEP) {
      this.audio.currentTime -= STEP
    }
    this.chaneProgress()
  }
  forward() {
    if (!this.items.length) {
      return
    }
    if (this.audio.currentTime < this.audio.duration - STEP) {
      this.audio.currentTime += STEP
    }
    this.chaneProgress()
  }
  _getRandomIndex() {
    const index = Math.floor(Math.random() * this.items.length)
    return index
  }
  toggleRandom() {
    this.random = !this.random
    this._savePlayerState()
  }
  toggleLoop() {
    this.loop = this.audio.loop = !this.loop
    this._savePlayerState()
  }
  toggleVolume() {
    this.volume = this.audio.volume = {
      0: 1,
      0.5: 0,
      1: 0.5,
    }[this.volume]
    this._savePlayerState()
  }
  mute() {
    this.audio.muted = !this.audio.muted
  }
  search(query = '') {
    this.searchCursorIndex = 0
    this.searchItems = query
      ? this.items.reduce((arr, item) => {
          if (item.name.toLowerCase().includes(query?.toLowerCase())) {
            arr.push(item)
          }
          return arr
        }, [])
      : []
  }
  searchCursorUp() {
    if (!this.searchItems.length) {
      this.searchCursorIndex = 0
      return
    }
    if (this.searchCursorIndex > 0) {
      this.searchCursorIndex--
    } else {
      this.searchCursorIndex = this.searchItems.length - 1
    }
  }
  searchCursorDown() {
    if (!this.searchItems.length) {
      this.searchCursorIndex = 0
      return
    }
    if (this.searchCursorIndex < this.searchItems.length - 1) {
      this.searchCursorIndex++
    } else {
      this.searchCursorIndex = 0
    }
  }
  seekTo(ratio) {
    if (!this.duration || !Number.isFinite(ratio)) {
      return
    }
    this.audio.currentTime = this.duration * ratio
    this.syncProgress()
  }
  syncProgress() {
    this.currentTime = safeNumber(this.audio.currentTime)
    this.duration = safeNumber(this.audio.duration)
  }
  get progressPercent() {
    if (!this.duration) {
      return 0
    }
    return Math.min(100, Math.max(0, (this.currentTime / this.duration) * 100))
  }
  _savePlayerState() {
    localStorage.setItem(
      PLAYER_STATE_KEY,
      JSON.stringify({
        random: this.random,
        loop: this.loop,
        volume: this.volume,
        index: this._getSafeIndex(this.index),
      })
    )
  }
  _getSafeIndex(index) {
    if (!this.items.length) {
      return 0
    }

    return Math.min(this.items.length - 1, Math.max(0, Number(index) || 0))
  }
}

const formatTime = (time) => {
  time = time || 0
  const min = Math.floor(time / 60)
  const sec = Math.floor(time % 60)
  return `${leadingZero(min)}:${leadingZero(sec)}`
}

const leadingZero = (num, n = 2) => {
  return String(num).padStart(n, '0')
}

const safeNumber = (num) => {
  return Number.isFinite(num) ? num : 0
}

function getSavedPlayerState(itemsLength) {
  const defaults = {
    random: true,
    loop: false,
    volume: 1,
    index: 0,
  }

  try {
    const savedPlayerState = JSON.parse(localStorage.getItem(PLAYER_STATE_KEY))
    return {
      random:
        typeof savedPlayerState?.random === 'boolean'
          ? savedPlayerState.random
          : defaults.random,
      loop:
        typeof savedPlayerState?.loop === 'boolean'
          ? savedPlayerState.loop
          : defaults.loop,
      volume: [0, 0.5, 1].includes(savedPlayerState?.volume)
        ? savedPlayerState.volume
        : defaults.volume,
      index: clampIndex(savedPlayerState?.index, itemsLength),
    }
  } catch {
    return defaults
  }
}

function clampIndex(index, itemsLength) {
  if (!itemsLength) {
    return 0
  }

  return Math.min(itemsLength - 1, Math.max(0, Number(index) || 0))
}

function isAudioFile(file) {
  return file.type.startsWith('audio') || AUDIO_EXT_REGEXP.test(file.name)
}
